import {
  EditEvent,
  EmissionEvent,
  ImportEvent,
  ShareholderRecord,
  State,
  TransactionEvent,
  ShareholderEvent,
  EstablishEvent,
  SplitEvent,
  RoundingStrategy,
  SpliceEvent,
  ShareDeletionDataEvent,
  moveSharesToClassEvent,
} from '../classes';
import {
  checkAllNumbers,
  getLastSharenumber,
  initNumbers,
  initNumbersFromNotes,
  initNumbersRaw,
  ShareNumbers,
} from '../numbers';
import { deleteAmount } from '../numbers/deleteAmount';
import { calculateShareNumbersToBeSold } from '../numbers/calculateSharesToBeSold';

import {
  transferAmountFromShareclassToShareclass,
  transferAmountFromTo,
} from '../numbers/transferAmountFromTo';

export type StateBuilder = (event: ShareholderEvent, previousState: State) => State;

export function _import({
  event,
  previousState,
  silent,
}: {
  event: ImportEvent;
  previousState: State;
  silent?: boolean;
}): State {
  let newRecords: ShareholderRecord[] = [...event.state.records];
  const newState: State = {
    ...previousState,
    records: newRecords,
  }; // Deep copy

  try {
    newRecords = initNumbers({
      records: newState.records,
      timeStamp: new Date(event.date).getTime(),
      eventRef: event._id || '',
      silent,
    });
    checkAllNumbers({ records: newRecords, allowGaps: true });
    newState.records = newRecords;
  } catch (err: any) {
    if (!silent) {
      console.log(err);
    }
    // Ignore errors
  }
  newState.highestIssuedShareNumber = getLastSharenumber(newState.records);
  return newState;
}

// Overwrite existing records with any in the eventState
// The user must be responisble for ensuring sharenumbers ate managed correctly.
export function _edit({
  event,
  previousState,
  silent,
}: {
  event: EditEvent;
  previousState: State;
  silent?: boolean;
}): State {
  const previousRecords = [...previousState.records.map((x: ShareholderRecord) => ({ ...x }))];

  const state = new State();

  for (const previousStateRecord of previousState.records) {
    const srcRecord = event.state.records.find(
      (d) =>
        d.entityId?.toString() === previousStateRecord.entityId?.toString() &&
        d.shareclassId?.toString() === previousStateRecord.shareclassId?.toString()
    );
    if (!srcRecord) {
      state.records.push(previousStateRecord);
    }
  }
  state.records = state.records.concat(event.state.records);
  try {
    const recordsWithNumbers = initNumbers({
      records: state.records,
      timeStamp: new Date(event.date || (event as any).createdAt).getTime(),
      eventRef: event._id || '',
      silent,
    });
    state.records = recordsWithNumbers;
  } catch (error: any) {
    if (!silent) {
      console.log(error);
    }
  }
  return state;
}

export function _establish({
  event,
  previousState,
  silent,
}: {
  event: EstablishEvent;
  previousState: State;
  silent: boolean;
}): State {
  let newState: State = event.state;
  let records = newState.records;
  try {
    if (records.find((record: ShareholderRecord): boolean => !record.notes)) {
      records = initNumbersRaw({
        records: records,
        timeStamp: new Date(event.date).getTime(),
        eventRef: event._id?.toString() || '',
        //Get the total amount of shares
        totalShares: 0,
      });
    } else {
      records = initNumbersFromNotes({
        records: records,
        timeStamp: new Date(event.date).getTime(),
        eventRef: event._id?.toString() || '',
      });
    }
    newState.records = records;
  } catch (error: any) {
    if (!silent) {
      console.log(error);
    }
  }
  newState.highestIssuedShareNumber = getLastSharenumber(newState.records);
  return newState;
}

function mergeEmissionRecordsWithPrevRecords(
  event: EmissionEvent,
  emissionRecords: ShareholderRecord[],
  previousState: State
): ShareholderRecord[] {
  const previousRecords: ShareholderRecord[] = [
    ...previousState.records.map((x: ShareholderRecord): ShareholderRecord => ({ ...x })),
  ];

  // Look at all previous state
  // If there is a new emission record in the event combine with previous state.
  let previousIndex = 0;
  for (const previousRecord of previousState.records) {
    // Read the previous state
    const srcRecordIndex = emissionRecords.findIndex(
      (emissionRecord: ShareholderRecord): boolean => {
        return (
          emissionRecord?.entityId?.toString() === previousRecord?.entityId?.toString() &&
          emissionRecord?.shareclassId?.toString() === previousRecord?.shareclassId?.toString()
        );
      }
    );

    if (srcRecordIndex >= 0) {
      const { newDstRecord } = transferAmountFromTo({
        src: emissionRecords[srcRecordIndex],
        dst: previousRecord,
        amount: emissionRecords[srcRecordIndex].amount,
        eventRef: event._id?.toString() || '',
        timeStamp: +new Date(event.date),
      });
      previousRecords[previousIndex] = newDstRecord;
    }
    previousIndex++;
  }

  // Now handle the records that did not exist in previous state.
  for (const srcRecord of emissionRecords) {
    const previousStateRecord = previousState.records.find(
      (d) =>
        d?.entityId?.toString() === srcRecord?.entityId?.toString() &&
        d?.shareclassId?.toString() === srcRecord?.shareclassId?.toString()
    );
    if (!previousStateRecord) {
      previousRecords.push(srcRecord);
    }
  }

  return previousRecords;
}

function split_splice({
  event,
  previousState,
  silent,
}: {
  event: SplitEvent;
  previousState: State;
  silent?: boolean;
}): State {
  const newState: State = {
    ...previousState,
    records: previousState.records,
    organizationId: previousState.organizationId,
  };
  let highestShareNumber = previousState.highestIssuedShareNumber || 0;

  const ratio = event.to / event.from;
  let smallestUnusedShareNumber = highestShareNumber || 0;

  previousState.records.sort((a, b) => b.amount - a.amount);

  const recs = previousState.records.map((record: ShareholderRecord) => {
    if (record.shareclassId?.toString() === event.shareclassId?.toString()) {
      const amount =
        event.strategy === RoundingStrategy.DOWN
          ? Math.floor(record.amount * ratio)
          : Math.ceil(record.amount * ratio);

      const numbers: ShareNumbers[] = record?.numbers || [];

      let newNumbers: ShareNumbers[] = [
        {
          from: smallestUnusedShareNumber,
          to: smallestUnusedShareNumber + amount,
          timeStamp: +new Date(event.date),
          eventRef: event._id?.toString(),
        },
      ];
      smallestUnusedShareNumber = smallestUnusedShareNumber + amount;
      return {
        ...record,
        amount:
          event.strategy === RoundingStrategy.DOWN
            ? Math.floor(record.amount * ratio)
            : Math.ceil(record.amount * ratio),
        numbers: newNumbers || numbers,
      };
    }

    return record;
  });
  newState.records = recs;
  newState.highestIssuedShareNumber = getLastSharenumber(newState.records);
  return newState;
}

export function _refreshShareNumbers({
  event,
  previousState,
  silent,
}: {
  event: ShareholderEvent;
  previousState: State;
  silent?: boolean;
}): State {
  const newState: State = {
    ...previousState,
    records: previousState.records,
    organizationId: previousState.organizationId,
  };
  let highestShareNumber = previousState.highestIssuedShareNumber || 0;

  previousState.records.sort((a, b) => b.amount - a.amount);

  const recs = previousState.records.map((record: ShareholderRecord) => {
    const numbers: ShareNumbers[] = record?.numbers || [];

    let newNumbers: ShareNumbers[] = [
      {
        from: highestShareNumber,
        to: highestShareNumber + record.amount,
        timeStamp: +new Date(event.date),
        eventRef: event._id?.toString(),
      },
    ];
    highestShareNumber = highestShareNumber + record.amount;
    return {
      ...record,
      numbers: newNumbers || numbers,
    };
  });
  newState.records = recs;
  newState.highestIssuedShareNumber = highestShareNumber;
  return newState;
}

export function _split(obj: { event: SplitEvent; previousState: State; silent?: boolean }): State {
  return split_splice(obj);
}
export function _splice(obj: {
  event: SpliceEvent;
  previousState: State;
  silent?: boolean;
}): State {
  return split_splice(obj);
}

export function _delete({
  event,
  previousState,
  silent,
}: {
  event: ShareDeletionDataEvent;
  previousState: State;
  silent?: boolean;
}): State {
  const newRecords = [...previousState.records];
  const deletionRecordIndex = newRecords.findIndex(
    (rec) =>
      rec.entityId?.toString() === event.sender?.toString() &&
      rec.shareclassId?.toString() === event.shareclassId?.toString()
  );
  const { newSrcRecord } = deleteAmount({
    src: previousState.records[deletionRecordIndex],
    amount: event.amount,
    eventRef: event._id?.toString() || '',
    timeStamp: +new Date(event.date),
  });
  newRecords[deletionRecordIndex] = newSrcRecord;
  return { ...previousState, records: newRecords };
}

export function _emission({
  event,
  previousState,
  silent,
}: {
  event: EmissionEvent;
  previousState: State;
  silent?: boolean;
}): State {
  let emissionRecords: ShareholderRecord[] = event.state.records.slice(); // Copy the event state
  try {
    // const totalAmounts = checkAllNumbers({ records: previousState.records, allowGaps: true });

    if (emissionRecords.find((record: ShareholderRecord): boolean => !record.notes)) {
      emissionRecords = initNumbersRaw({
        records: emissionRecords,
        timeStamp: new Date(event.date).getTime(),
        eventRef: event._id?.toString() || '',
        //Get the total amount of shares
        totalShares: previousState.highestIssuedShareNumber || 0,
      });
    } else {
      emissionRecords = initNumbersFromNotes({
        records: emissionRecords,
        timeStamp: new Date(event.date).getTime(),
        eventRef: event._id?.toString() || '',
      });
    }
  } catch (error: any) {
    if (!silent) {
      console.log(error);
    }
  }

  let records;
  try {
    records = mergeEmissionRecordsWithPrevRecords(event, emissionRecords, previousState);
    checkAllNumbers({ records, allowGaps: true });
  } catch (error: any) {
    if (!silent) {
      console.log(error);
    }
    records = removeNumbersFromRecords(records);
  }
  previousState.highestIssuedShareNumber = getLastSharenumber(records);
  return { ...previousState, records };
}
//
// If check all numbers doesn't work at any point, we want to tidy up the records (remove the numbers that were generated before)
function removeNumbersFromRecords(records: ShareholderRecord[]): ShareholderRecord[] {
  return records.map(
    (record: ShareholderRecord): ShareholderRecord => ({
      ...record,
      numbers: null as any,
    })
  );
}

//
export function _transaction({
  event,
  previousState,
  silent,
}: {
  event: TransactionEvent;
  previousState: State;
  silent?: boolean;
}): State {
  // FInd existing records.
  // Create recipient if need be.
  const newRecords = [...previousState.records];

  const senderRecordIndex = newRecords.findIndex(
    (record) =>
      record.entityId?.toString() === event.sender?.toString() &&
      record.shareclassId?.toString() === event.shareclassId?.toString()
  );

  const recipientRecordIndex = newRecords.findIndex(
    (record) =>
      record.entityId?.toString() === event.recipient?.toString() &&
      record.shareclassId?.toString() === event.shareclassId?.toString()
  );

  let recipientRecord: ShareholderRecord;
  if (recipientRecordIndex < 0) {
    recipientRecord = {
      entityId: event.recipient,
      amount: 0,
      numbers: [],
      shareclassId: event.shareclassId,
    };
  } else {
    recipientRecord = previousState.records[recipientRecordIndex];
  }

  const { newDstRecord, newSrcRecord } = transferAmountFromTo({
    src: previousState.records[senderRecordIndex],
    dst: recipientRecord,
    amount: event.amount,
    eventRef: event._id?.toString() || '',
    timeStamp: +new Date(event.date),
  });

  newRecords[senderRecordIndex] = newSrcRecord;

  if (recipientRecordIndex >= 0) {
    newRecords[recipientRecordIndex] = newDstRecord;
  } else {
    newRecords.push(newDstRecord);
  }
  return { ...previousState, records: newRecords };
}

export function _moveSharesFromShareclass({
  event,
  previousState,
  silent,
}: {
  event: moveSharesToClassEvent;
  previousState: State;
  silent?: boolean;
}): State {
  const newRecords = [...previousState.records];
  const senderRecordIndex = newRecords.findIndex(
    (rec) =>
      rec.entityId?.toString() === event.sender?.toString() &&
      rec.shareclassId?.toString() === event.shareclassId?.toString()
  );

  const senderRecordCopy = {
    ...newRecords[senderRecordIndex],
  };

  if (senderRecordCopy.amount < event.amount) return;

  const addRecordIndex = newRecords.findIndex(
    (rec) =>
      rec.entityId?.toString() === event.sender?.toString() &&
      rec.shareclassId?.toString() === event.toShareclassId?.toString()
  );
  if (!senderRecordCopy?.numbers) {
    newRecords[senderRecordIndex].amount -= event.amount;
    if (addRecordIndex === -1) {
      //Create a new record if this user has none of this shares from before

      newRecords.push({
        entityId: event.sender,
        amount: event.amount,
        shareclassId: event.toShareclassId,
      });
    } else {
      //if the record already exists, create copy and add the amount and numbers

      newRecords[addRecordIndex] = { ...newRecords[addRecordIndex] };
      newRecords[addRecordIndex].amount += event.amount;
    }
    // throw 'Moveshares: No numbers on sender';
    return { ...previousState, records: newRecords };
  }

  //Calculate what the original record has left after moving some of their shares
  const { remainingNumbers, soldNumbers } = calculateShareNumbersToBeSold({
    // is this the correct to use here?
    numbers: senderRecordCopy.numbers,
    amount: event.amount,
    eventRef: event._id?.toString() || '',
    timeStamp: +new Date(event.date),
  });

  if (addRecordIndex === -1) {
    //Create a new record if this user has none of this shares from before

    newRecords.push({
      entityId: event.sender,
      amount: event.amount,
      numbers: soldNumbers,
      shareclassId: event.toShareclassId,
    });
  } else {
    //if the record already exists, create copy and add the amount and numbers
    const toShareclassRecords = newRecords.filter(
      (rec) => rec.shareclassId?.toString() === event.toShareclassId?.toString()
    );

    let maxNumber = 0;
    toShareclassRecords.forEach((record) => {
      record.numbers?.forEach((number) => {
        if (number.to > maxNumber) {
          maxNumber = number.to;
        }
      });
    });

    const newSoldNumbers: ShareNumbers[] = soldNumbers.map((number) => ({
      ...number,
      from: maxNumber + 1,
      to: maxNumber + (number.to - number.from + 1),
    }));

    maxNumber += event.amount;

    newRecords[addRecordIndex].amount += event.amount;
    newRecords[addRecordIndex] = { ...newRecords[addRecordIndex] };
    newRecords[addRecordIndex].numbers = [
      ...previousState.records[addRecordIndex].numbers,
      ...newSoldNumbers,
    ];
    //will not give correct number range since this is from previous shareclass
    //newRecords[addRecordIndex].numbers = [
    //  ...previousState.records[addRecordIndex].numbers,
    //  ...soldNumbers,
    //];
  }
  //set original record amount and numbers
  senderRecordCopy.amount -= event.amount;
  if (remainingNumbers.length === 0) {
    newRecords.splice(senderRecordIndex, 1);

    return { ...previousState, records: newRecords };
  }
  senderRecordCopy.numbers = remainingNumbers;

  newRecords[senderRecordIndex] = senderRecordCopy;
  newRecords[senderRecordIndex] = senderRecordCopy;

  return { ...previousState, records: newRecords };
}
